<template>
  <div class="time-drum-picker">
    <div class="drum-cols">
      <div
        ref="hourColEl"
        class="drum-col"
        @scroll="onHourScroll"
      >
        <div class="drum-spacer" />
        <div
          v-for="h in hours"
          :key="`h-${h}`"
          :class="['drum-cell', { 'is-selected': h === selectedHour }]"
          :data-hour="h"
          @click="selectHour(h)"
        >
          {{ pad2(h) }}
        </div>
        <div class="drum-spacer" />
      </div>
      <div class="drum-sep">
        :
      </div>
      <div
        ref="minuteColEl"
        class="drum-col"
        @scroll="onMinuteScroll"
      >
        <div class="drum-spacer" />
        <div
          v-for="m in minutes"
          :key="`m-${m}`"
          :class="['drum-cell', { 'is-selected': m === selectedMinute }]"
          :data-minute="m"
          @click="selectMinute(m)"
        >
          {{ pad2(m) }}
        </div>
        <div class="drum-spacer" />
      </div>
    </div>
    <div class="drum-overlay" />
  </div>
</template>

<script>
import {
  ref, computed, onMounted, watch, nextTick,
} from '@vue/composition-api'

const pad2 = n => String(n).padStart(2, '0')
const CELL_HEIGHT = 40

export default {
  name: 'TimeDrumPicker',
  props: {
    value: { type: Date, default: null },
    step: { type: Number, default: 5 },
  },
  emits: ['input'],
  setup(props, { emit }) {
    const hourColEl = ref(null)
    const minuteColEl = ref(null)

    const hours = computed(() => Array.from({ length: 24 }, (_, i) => i))
    const minutes = computed(() => {
      const len = Math.floor(60 / props.step)

      return Array.from({ length: len }, (_, i) => i * props.step)
    })

    const snapMinute = (m, step) => {
      const stepped = Math.round(m / step) * step

      return Math.min(60 - step, Math.max(0, stepped))
    }

    const currentDate = computed(() => (props.value instanceof Date ? props.value : new Date()))
    const selectedHour = ref(currentDate.value.getHours())
    const selectedMinute = ref(snapMinute(currentDate.value.getMinutes(), props.step))

    const scrollToValue = (el, idx) => {
      if (!el) return
      // eslint-disable-next-line no-param-reassign
      el.scrollTop = idx * CELL_HEIGHT
    }

    const syncScroll = async () => {
      await nextTick()
      const hIdx = hours.value.indexOf(selectedHour.value)
      const mIdx = minutes.value.indexOf(selectedMinute.value)
      scrollToValue(hourColEl.value, Math.max(0, hIdx))
      scrollToValue(minuteColEl.value, Math.max(0, mIdx))
    }

    const emitChange = () => {
      const base = props.value instanceof Date ? new Date(props.value.getTime()) : new Date()
      base.setHours(selectedHour.value, selectedMinute.value, 0, 0)
      emit('input', base)
    }

    let hourTimer = null
    let minuteTimer = null

    const onHourScroll = () => {
      if (hourTimer) clearTimeout(hourTimer)
      hourTimer = setTimeout(() => {
        const el = hourColEl.value
        if (!el) return
        const idx = Math.round(el.scrollTop / CELL_HEIGHT)
        const next = hours.value[Math.min(hours.value.length - 1, Math.max(0, idx))]
        if (next !== selectedHour.value) {
          selectedHour.value = next
          emitChange()
        }
        // snap
        scrollToValue(el, idx)
      }, 120)
    }

    const onMinuteScroll = () => {
      if (minuteTimer) clearTimeout(minuteTimer)
      minuteTimer = setTimeout(() => {
        const el = minuteColEl.value
        if (!el) return
        const idx = Math.round(el.scrollTop / CELL_HEIGHT)
        const next = minutes.value[Math.min(minutes.value.length - 1, Math.max(0, idx))]
        if (next !== selectedMinute.value) {
          selectedMinute.value = next
          emitChange()
        }
        scrollToValue(el, idx)
      }, 120)
    }

    const selectHour = h => {
      selectedHour.value = h
      emitChange()
      syncScroll()
    }

    const selectMinute = m => {
      selectedMinute.value = m
      emitChange()
      syncScroll()
    }

    watch(() => props.value, v => {
      if (!(v instanceof Date)) return
      const h = v.getHours()
      const m = snapMinute(v.getMinutes(), props.step)
      if (h !== selectedHour.value || m !== selectedMinute.value) {
        selectedHour.value = h
        selectedMinute.value = m
        syncScroll()
      }
    })

    onMounted(() => {
      syncScroll()
    })

    return {
      hourColEl,
      minuteColEl,
      hours,
      minutes,
      selectedHour,
      selectedMinute,
      onHourScroll,
      onMinuteScroll,
      selectHour,
      selectMinute,
      pad2,
    }
  },
}
</script>

<style scoped>
.time-drum-picker {
  position: relative;
  display: inline-block;
  user-select: none;
}
.drum-cols {
  display: flex;
  align-items: center;
  gap: 4px;
}
.drum-col {
  width: 64px;
  height: 120px; /* CELL_HEIGHT * 3 */
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  text-align: center;
  border: 1px solid rgba(127, 127, 127, 0.24);
  border-radius: 6px;
  background: var(--v-background-base);
}
.drum-col::-webkit-scrollbar {
  display: none;
}
.drum-cell {
  height: 40px;
  line-height: 40px;
  font-size: 1.25rem;
  scroll-snap-align: center;
  cursor: pointer;
  color: var(--v-on-surface-base, currentColor);
}
.drum-cell.is-selected {
  font-weight: 700;
  color: var(--v-primary-base);
}
.drum-spacer {
  height: 40px;
  scroll-snap-align: center;
}
.drum-sep {
  font-size: 1.5rem;
  font-weight: 700;
  padding: 0 4px;
}
.drum-overlay {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 40px;
  border-top: 1px solid rgba(127, 127, 127, 0.4);
  border-bottom: 1px solid rgba(127, 127, 127, 0.4);
}
</style>
